import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  ArrowRight,
  Baby,
  Car,
  Check,
  ClipboardList,
  Factory,
  Gauge,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  Wrench,
} from 'lucide-react';
import './styles.css';

const assetVersion = '20260429-r3';

const services = [
  {
    icon: Wrench,
    title: 'Кузовний ремонт',
    text: 'Оцінюємо пошкодження, пояснюємо обсяг робіт і координуємо ремонт так, щоб власник розумів бюджет, строки та очікуваний результат.',
  },
  {
    icon: Factory,
    title: 'Управління парком автомобілів',
    text: 'Беремо на себе сервісну рутину автопарку: ТО, ремонти, запчастини, діагностику, облік витрат, сервісну історію і регулярну звітність.',
  },
  {
    icon: Baby,
    title: 'Авто няня',
    text: 'Персональний супровід авто: запис на сервіс, контроль робіт, сезонні шини, нагадування про ТО, консультації та допомога з дрібними питаннями.',
  },
  {
    icon: Car,
    title: 'Підбір автомобіля',
    text: 'Підбираємо вживане авто під реальні потреби: перший автомобіль, сімейний варіант, авто для роботи або надійну машину в бюджеті.',
  },
  {
    icon: ClipboardList,
    title: 'Автозапчастини та аксесуари',
    text: 'Підбираємо оригінальні, ліцензійні та якісні аналогові деталі за OEM-номером, моделлю, роком випуску і реальною задачею.',
  },
];

const checkpoints = [
  'Шукаємо деталь за OEM-номером, VIN, моделлю або описом проблеми.',
  'Пояснюємо, коли варто брати оригінал, а коли достатньо якісного аналога.',
  'Працюємо з кузовними деталями, ходовою, фільтрами, ременями, гальмами та аксесуарами.',
  'Постачання можливе зі складу або під замовлення з Європи, Еміратів чи Америки.',
];

const detailBlocks = [
  {
    id: 'car-selection',
    kicker: 'Підбір автомобіля',
    title: 'Надійне авто без прихованих проблем і зайвих витрат',
    lead: 'Допомагаємо підібрати вживане авто під ваші реальні потреби: перший автомобіль, сімейний варіант, авто для роботи або максимально надійну машину в обраному бюджеті. Працюємо в Києві та Київській області.',
    columns: [
      {
        title: 'Що ми робимо',
        items: [
          'Знаходимо варіанти відповідно до ваших критеріїв: бюджет, рік, пробіг, тип кузова тощо.',
          'Проводимо комплексну перевірку кузова, лакофарбового покриття та технічного стану.',
          'Виконуємо компʼютерну діагностику.',
          'Даємо чесну оцінку ризику та майбутніх витрат на авто.',
          'Допомагаємо з торгом і оформленням угоди.',
        ],
      },
      {
        title: 'Чому варто звернутися до MMC',
        items: [
          'Практичний підхід без ілюзій і красивих історій.',
          'Чітка та чесна оцінка реального стану автомобіля.',
          'План дій: що перевірити негайно, що відкласти, на що звернути увагу.',
          'Прозора вартість послуги.',
          'Досвід роботи з різними марками та станами авто.',
        ],
      },
      {
        title: 'Як відбувається підбір',
        items: [
          'Обговорення завдання: яке авто шукаєте і який бюджет.',
          'Пошук варіантів: відбираємо реальні оголошення під ваші вимоги.',
          'Детальна перевірка: виїзд на огляд і повна діагностика.',
          'Рекомендація та супровід: чесний висновок, допомога з торгом та оформленням.',
        ],
      },
    ],
    result: 'Результат — ви купуєте авто з відкритими очима, без неприємних сюрпризів після покупки.',
  },
  {
    id: 'fleet',
    kicker: 'Управління парком автомобілів',
    title: 'Сервісна рутина автопарку під контролем MMC',
    lead: 'Беремо на себе всю сервісну рутину вашого автопарку, щоб ви могли зосередитися на бізнесі. Допомагаємо підприємствам та компаніям підтримувати автопарк у робочому стані з мінімальними витратами часу та нервів.',
    columns: [
      {
        title: 'Що входить у послугу',
        items: [
          'Планове технічне обслуговування та ремонти.',
          'Підбір, закупівля та контроль якості запчастин.',
          'Комплексна діагностика та оперативне усунення несправностей.',
          'Облік усіх робіт, витрат і сервісної історії кожного авто.',
          'Зрозуміла комунікація та регулярна звітність.',
          'Контроль термінів і рекомендації щодо оптимізації витрат.',
        ],
      },
      {
        title: 'Результат для вас',
        items: [
          'Автопарк завжди на ходу.',
          'Прогнозовані витрати на обслуговування.',
          'Значна економія часу керівника та логіста.',
          'Прозорість і контроль над кожним автомобілем.',
        ],
      },
      {
        title: 'Чому обирають MMC',
        items: [
          'Практичний підхід без зайвої бюрократії.',
          'Пряма робота з перевіреними СТО та постачальниками.',
          'Чітке фіксування всіх робіт і витрат.',
          'Індивідуальний підхід під розмір вашого парку: від 3-5 авто і більше.',
          'Досвід роботи з легковими, комерційними авто та спеціальною технікою.',
        ],
      },
    ],
    result: 'Ви бачите стан, витрати і пріоритети по кожному автомобілю без ручного хаосу в сервісних питаннях.',
  },
  {
    id: 'auto-nanny',
    kicker: 'Авто няня',
    title: 'Ваш автомобіль під надійним доглядом',
    lead: 'Авто няня підходить власникам, які не хочуть самостійно контролювати сервіс, майстрів, строки, дрібні поломки і регулярні нагадування.',
    columns: [
      {
        title: 'Що входить у послугу',
        items: [
          'Запис на сервісне обслуговування та ремонт.',
          'Контроль якості робіт і спілкування з майстрами.',
          'Заміна та зберігання сезонних шин.',
          'Консультації щодо будь-яких питань по авто.',
          'Нагадування про ТО, заміну рідин, страховку, техогляд тощо.',
          'Допомога з дрібними поломками та оперативними питаннями.',
          'Контроль термінів і вартості робіт.',
        ],
      },
      {
        title: 'Результат для вас',
        items: [
          'Автомобіль завжди в порядку.',
          'Немає пропущених ТО, сюрпризів і зайвого головного болю.',
          'Ви спокійні, бо процес контролює MMC.',
        ],
      },
      {
        title: 'Чому це зручно',
        items: [
          'Повний супровід авто в одному місці.',
          'Ми контролюємо процес замість вас.',
          'Прозорість: завжди знаєте, що робиться і за скільки.',
          'Економія часу та нервів.',
          'Підходить як для приватних авто, так і для невеликих автопарків.',
        ],
      },
    ],
    result: 'Готові передати свій автомобіль під надійний догляд? Опишіть задачу, і MMC збере зрозумілий маршрут дій.',
  },
];

const primaryPhone = '0674428482';
const primaryPhoneIntl = '+380674428482';
const secondaryPhone = '0504425077';

function App() {
  return (
    <main>
      <section className="hero" aria-label="MMC landing hero">
        <div className="heroMedia" />
        <header className="topbar">
          <a className="brand" href="#top" aria-label="MMC home">
            <img src={`/mmc-logo-v${assetVersion}.svg`} alt="MMC сервіс" />
            <span className="brandText">автомобільний офіс</span>
          </a>
          <nav className="nav" aria-label="Main navigation">
            <a href="#services">Послуги</a>
            <a href="#details">Детально</a>
            <a href="#parts">Запчастини</a>
            <a href="#contact">Контакт</a>
          </nav>
        </header>

        <div className="heroContent" id="top">
          <p className="eyebrow">Київ · ремонт · запчастини · автопарк</p>
          <h1>MMC бере автомобільні задачі під контроль.</h1>
          <p className="lead">
            Підбір запчастин, кузовний ремонт, авто няня, підбір автомобіля і супровід автопарків для бізнесу в одному відповідальному сервісі.
          </p>
          <div className="heroActions">
            <a className="primaryAction" href="#contact">
              Обговорити задачу <ArrowRight size={18} />
            </a>
            <a className="secondaryAction" href="#services">Подивитися послуги</a>
          </div>
        </div>

        <aside className="heroPanel" aria-label="MMC service promise">
          <Sparkles size={20} />
          <span>Не просто продаємо деталь. Перевіряємо, що саме потрібно машині, де ризик, а де можна не переплачувати.</span>
        </aside>
      </section>

      <section className="intro">
        <div>
          <p className="sectionKicker">Позиціонування</p>
          <h2>Автомобільний офіс для власника, якому потрібне рішення, а не чергова порада.</h2>
        </div>
        <p>
          MMC починався з практичної експертизи в Mitsubishi, OEM-каталогах, оригінальних та ліцензійних запчастинах.
          Сьогодні ця база працює ширше: ми допомагаємо приватним власникам і компаніям швидше приймати правильні рішення по ремонту, сервісу, купівлі авто та деталях.
        </p>
      </section>

      <section className="services" id="services">
        <div className="sectionHeader">
          <p className="sectionKicker">Напрями</p>
          <h2>Пʼять напрямів, які закривають більшість автомобільних питань</h2>
        </div>
        <div className="serviceGrid">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <article className="serviceCard" key={service.title}>
                <Icon size={24} />
                <h3>{service.title}</h3>
                <p>{service.text}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="serviceDetails" id="details">
        <div className="sectionHeader">
          <p className="sectionKicker">Детально</p>
          <h2>Ключові послуги без загальних обіцянок: що саме робить MMC</h2>
        </div>
        <div className="detailStack">
          {detailBlocks.map((block) => (
            <article className="detailBlock" id={block.id} key={block.id}>
              <div className="detailIntro">
                <p className="sectionKicker">{block.kicker}</p>
                <h3>{block.title}</h3>
                <p>{block.lead}</p>
              </div>
              <div className="detailColumns">
                {block.columns.map((column) => (
                  <div className="detailColumn" key={column.title}>
                    <h4>{column.title}</h4>
                    <ul>
                      {column.items.map((item) => (
                        <li key={item}>
                          <Check size={16} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="detailResult">{block.result}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="partsBand" id="parts">
        <div className="partsCopy">
          <p className="sectionKicker">Запчастини</p>
          <h2>Оригінал, ліцензія або якісний аналог: вибір має бути технічно обґрунтованим.</h2>
          <p>
            Для підбору важливі OEM-номер, VIN, модель, рік, ринок автомобіля і симптом проблеми. Якщо каталожний номер
            невідомий, MMC допоможе ідентифікувати деталь, підібрати варіанти та пояснити різницю між ціною, ресурсом і ризиком.
          </p>
        </div>
        <div className="checklist">
          {checkpoints.map((item) => (
            <div className="checkItem" key={item}>
              <Check size={18} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="decision">
        <div className="decisionCard">
          <ShieldCheck size={28} />
          <h2>Підбір авто без романтики оголошень</h2>
          <p>
            Перший автомобіль, найгарніша машина в бюджеті або варіант, який не буде ламатися щомісяця, це різні сценарії.
            MMC перетворює бажання в критерії перевірки: кузов, техніка, історія, ціна входу і майбутні витрати.
          </p>
        </div>
        <div className="metrics" aria-label="MMC positioning">
          <div>
            <Gauge size={22} />
            <strong>Оцінка ризику</strong>
            <span>по кузову, техніці, ціні та майбутніх витратах</span>
          </div>
          <div>
            <ClipboardList size={22} />
            <strong>План дій</strong>
            <span>що дивитися, що міняти, що відкласти</span>
          </div>
        </div>
      </section>

      <section className="contact" id="contact">
        <div>
          <p className="sectionKicker">Тестова версія 2026</p>
          <h2>Опишіть автомобільну задачу. MMC збере маршрут рішення.</h2>
          <div className="contactMeta">
            <span><MapPin size={18} /> Київ, вулиця Полярна 19Б</span>
            <span><Phone size={18} /> {primaryPhone} · {secondaryPhone}</span>
          </div>
        </div>
        <div className="contactActions" aria-label="MMC contacts">
          <a className="phoneAction" href={`tel:${primaryPhoneIntl}`}>
            <Phone size={20} />
            Подзвонити
          </a>
          <a className="phoneAction" href={`viber://chat?number=%2B${primaryPhoneIntl.slice(1)}`}>
            <MessageCircle size={20} />
            Viber
          </a>
          <a className="phoneAction" href={`tg://resolve?phone=${primaryPhoneIntl.slice(1)}`}>
            <Send size={20} />
            Telegram
          </a>
          <a className="phoneAction phoneActionMuted" href="mailto:zapros@mmc.org.ua">
            <Mail size={20} />
            Email
          </a>
        </div>
      </section>
    </main>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
